import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../../theme';
import { CheckUrl } from '../../../assets';
import { UIText, UITextSize } from '../../typography';

export type CheckboxItemType = {
  label: string;
  isChecked?: boolean;
  count?: number | string;
  highlight?: boolean;
};

export type CheckboxSize = 'sm' | 'lg';

export type CheckboxProps = CheckboxItemType & {
  labelSize?: UITextSize;
  checkboxSize?: CheckboxSize;
  controlled?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};
const StyledUiText = styled(UIText)<{ isChecked?: boolean }>`
  font-weight: ${({ isChecked }) => (isChecked ? 'bold' : 'normal')};
`;
const Label = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacings[2].rem};
`;

const TextContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const CountPill = styled.div`
  padding: ${theme.spacings[0.5].rem} ${theme.spacings[1.5].rem};
  background: ${theme.colors.neutrals.transparentGrays[7]};
  border-radius: ${theme.spacings[3].rem};
`;

const Count = styled(UIText)<{ isChecked?: boolean }>`
  color: ${theme.colors.neutrals.solidGrays[2]};
  font-weight: ${({ isChecked }) => (isChecked ? 'bold' : 'normal')};
`;

const Input = styled.input<{ checkboxSize?: CheckboxSize }>`
  /* hide default */
  appearance: none;
  margin: 0;

  /* custom */
  flex-shrink: 0;
  background-color: ${theme.colors.neutrals.solidGrays[8]};
  transition: background-color 200ms ease-in;
  border: 1px solid ${theme.colors.neutrals.border.light};
  border-radius: 2px;
  box-sizing: border-box;

  font: inherit;
  color: currentColor;
  ${({ checkboxSize }) =>
    checkboxSize === 'lg'
      ? css`
          width: 24px;
          height: 24px;
        `
      : css`
          width: 16px;
          height: 16px;
        `}

  &:hover {
    background-color: ${theme.colors.neutrals.solidGrays[7]};
  }

  &:checked {
    background-color: ${theme.colors.primary.white};

    &::before {
      content: '';
      mask: url(${CheckUrl}) no-repeat 50% 50%;
      mask-size: cover;
      display: inline-block;
      ${({ checkboxSize }) =>
        checkboxSize === 'lg'
          ? css`
              width: 22px;
              height: 22px;
            `
          : css`
              width: 14px;
              height: 14px;
            `}
      background-color: ${theme.colors.tints.graysBlue['_1']};
    }
  }
`;

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  count,
  isChecked,
  highlight = false,
  onChange,
  controlled,
  checkboxSize = 'sm',
  labelSize = 'sm',
}) => {
  const [checked, setChecked] = useState(false);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    !controlled && setChecked(event.target.checked);
    onChange(event);
  };

  return (
    <Label>
      <Input
        checked={controlled ? isChecked : checked}
        checkboxSize={checkboxSize}
        name={label}
        onChange={handleChange}
        type="checkbox"
      />
      <TextContainer>
        <StyledUiText
          isChecked={isChecked || highlight}
          className="js-label"
          size={labelSize}
        >
          {label}
        </StyledUiText>
        {count && (
          <CountPill>
            <Count isChecked={isChecked} size={'xs'}>
              {count}
            </Count>
          </CountPill>
        )}
      </TextContainer>
    </Label>
  );
};
